import { CalculatedValue } from "../../models/text/CalculatedValue";
import { LayoutLine } from "./SplitMeasuredTokens";
import { ISplitOptions } from "../../models/text/ISplitOptions";
import { SPLIT_ELEMENT_CLASS } from "../../models/text/SplitElementClass";

/**
 * Builds a DOM tree representation of the given layout lines based on the provided options.
 *
 * @param layoutLines - An array of `LayoutLine` objects, each representing a line of text
 *                       with associated words and characters.
 * @param options - An `ISplitOptions` object that specifies how the DOM tree should be constructed,
 *                  including options for line, word, and character-level styling and structure.
 *
 * @returns A `DocumentFragment` containing the constructed DOM tree.
 *
 * The function processes the layout lines and creates a hierarchical DOM structure:
 * - If line-level options are enabled, each line is wrapped in a `<span>` element with the class `-s-line`.
 * - If word-level options are enabled, each word is wrapped in a `<span>` element with the class `-s-word`.
 * - If character-level options are enabled, each character is wrapped in a `<span>` element with the class `-s-char`.
 *
 * The function also applies styles and attributes to the created elements based on the provided options
 * and the calculated values in the layout lines, words, and characters.
 *
 * Special handling is applied for tokens of type `element`, where the original DOM node is cloned
 * and appended directly to the container.
 *
 * Spaces between words are preserved using non-breaking space (`\u00a0`) text nodes.
 */
export function BuildDOMTree(layoutLines: LayoutLine[], options: ISplitOptions): DocumentFragment {
  const fragment = document.createDocumentFragment();
  let localCharIndex = 0;
  const hasLine =
    hasOption(options, "line") || hasOption(options, "charLine") || hasOption(options, "wordLine");
  let wordsCount = 0;
  layoutLines.forEach((line) => {
    wordsCount += line.words.length;
  });
  wordsCount--;

  layoutLines.forEach((line, li) => {
    const isLastInLayout = li === layoutLines.length - 1;

    let container: HTMLElement | DocumentFragment = fragment;

    if (hasLine) {
      container = document.createElement("span");
      container?.setAttribute("aria-hidden", "true");
      container.classList.add("-s-line");
      if (line.isBeforeElement) {
        container.classList.add(SPLIT_ELEMENT_CLASS.BEFORE_ELEMENT);
      }
      if (line.isAfterElement) {
        container.classList.add(SPLIT_ELEMENT_CLASS.AFTER_ELEMENT);
      }
      container.style.setProperty("--line-index", String(line.lineIndex));
      container.style.setProperty("--word-total", String(line.words.length));
      applyStyles(container, (line as any).calculatedValues, options);
    }

    line.words.forEach((word, wi) => {
      if (hasLine) {
        wordsCount = line.words.length - 1;
      }
      const isLast = wi === wordsCount;

      if (word.chars.length === 1 && word.chars[0].token.type === "element") {
        const original = word.chars[0].token.node.cloneNode(true) as HTMLElement;
        container.appendChild(original);
        if (!isLast) container.appendChild(document.createTextNode("\u00a0"));
        return;
      }

      const hasWord =
        hasOption(options, "word") ||
        hasOption(options, "charWord") ||
        hasOption(options, "wordLine");
      const wordEl = hasWord ? document.createElement("span") : (container as HTMLElement);
      if (hasWord) {
        wordEl?.setAttribute("aria-hidden", "true");
        wordEl.classList.add("-s-word");
        if (word.isBeforeElement) {
          wordEl.classList.add(SPLIT_ELEMENT_CLASS.BEFORE_ELEMENT);
        }
        if (word.isAfterElement) {
          wordEl.classList.add(SPLIT_ELEMENT_CLASS.AFTER_ELEMENT);
        }
        wordEl.style.setProperty("--word-index", String(word.wordIndexGlobal));
        wordEl.style.setProperty("--char-total", String(word.chars.length));
        applyStyles(wordEl, (word as any).calculatedValues, options);
      }

      const hasChar =
        hasOption(options, "char") ||
        hasOption(options, "charLine") ||
        hasOption(options, "charWord");

      if (hasChar) {
        word.chars.forEach((char) => {
          if (char.char === " " || char.char === "\t") return;
          const charNode = document.createElement("span");
          charNode?.setAttribute("aria-hidden", "true");
          const charEl = charNode as HTMLElement;
          charEl.classList.add("-s-char");
          if (char.isBeforeElement) {
            charEl.classList.add(SPLIT_ELEMENT_CLASS.BEFORE_ELEMENT);
          }
          if (char.isAfterElement) {
            charEl.classList.add(SPLIT_ELEMENT_CLASS.AFTER_ELEMENT);
          }
          charEl.textContent = char.char;
          charEl.style.setProperty("--char-index", String(localCharIndex++));
          applyStyles(charEl, (char as any).calculatedValues, options);
          wordEl.appendChild(charNode);
        });
      } else {
        const charNode = document.createTextNode(word.chars.map((c) => c.char).join(""));
        wordEl.appendChild(charNode);
      }

      if (hasWord) {
        container.appendChild(wordEl);
      }

      // console.log(layoutLines, hasLine, isLast, isLastInLayout);

      if (hasLine) {
        if (!isLast) {
          wordEl.appendChild(document.createTextNode("\u00a0"));
        } else {
          if (!isLastInLayout) {
            container.appendChild(document.createElement("br"));
          }
        }
      } else {
        if (!isLast) {
          wordEl.appendChild(document.createTextNode("\u00a0"));
        }
      }
    });

    if (hasLine) fragment.appendChild(container as HTMLElement);
  });

  return fragment;
}

/**
 * Applies calculated styles to a given HTML element based on the provided values and options.
 *
 * @param el - The HTML element to which the styles will be applied.
 * @param calculatedValues - An array of calculated values containing style information, or `undefined` if no styles are to be applied.
 * @param options - The options object that determines which styles are enabled and how they should be aligned.
 *
 * @remarks
 * This function iterates over the `calculatedValues` array and applies styles to the element
 * only if the corresponding option is enabled. The style property name is generated dynamically
 * using the `generateVariableName` function.
 *
 * @example
 * ```typescript
 * const element = document.createElement('div');
 * const calculatedValues = [
 *   { type: 'color', align: 'left', value: '#ff0000' },
 *   { type: 'margin', align: 'top', value: '10px' }
 * ];
 * const options = { enableColor: true, enableMargin: true };
 * applyStyles(element, calculatedValues, options);
 * ```
 */
function applyStyles(
  el: HTMLElement,
  calculatedValues: CalculatedValue[] | undefined,
  options: ISplitOptions
) {
  if (!calculatedValues) return;
  for (const val of calculatedValues) {
    if (!isOptionEnabled(val.type, val.align, options)) continue;
    const varName = generateVariableName(val.type, val.align);
    el.style.setProperty(varName, String(val.value));
  }
}

/**
 * Determines whether a specific option is enabled based on the provided type, alignment, and options.
 *
 * @param type - The type of the option to check within the options object.
 * @param align - The alignment string to match against the option's alignment.
 *                If the alignment starts with "random", it checks for options
 *                whose alignment also starts with "random".
 * @param options - An object containing various option groups, where each group
 *                  is an array of items with an `align` property.
 * @returns `true` if the specified option is enabled, otherwise `false`.
 */
function isOptionEnabled(type: string, align: string, options: ISplitOptions): boolean {
  const group = options[type as keyof ISplitOptions] ?? [];
  return (
    Array.isArray(group) &&
    group.some((item) =>
      align.startsWith("random") ? item.align.startsWith("random") : item.align === align
    )
  );
}

/**
 * Generates a CSS variable name based on the provided type and alignment.
 *
 * The function normalizes the `align` parameter by checking if it starts with "random".
 * If it does, the alignment is normalized to "random"; otherwise, the original alignment is used.
 * The resulting variable name is constructed in the format `--<type>-<normalizedAlign>`.
 *
 * @param type - The type of the variable, typically representing a category or context.
 * @param align - The alignment or variation, which may start with "random" or be a specific value.
 * @returns A string representing the generated CSS variable name.
 */
function generateVariableName(type: string, align: string): string {
  const norm = align.startsWith("random") ? "random" : align;
  return `--${type}-${norm}`;
}

/**
 * Checks if a specific key in the given options object is an array and contains at least one element.
 *
 * @param options - The options object implementing the `ISplitOptions` interface.
 * @param key - The key to check within the options object.
 * @returns `true` if the value associated with the key is an array with at least one element, otherwise `false`.
 */
function hasOption(options: ISplitOptions, key: keyof ISplitOptions): boolean {
  return Array.isArray(options[key]) && options[key]!.length > 0;
}
